import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Users, DollarSign, Mail, TrendingUp, Crown, 
  Globe, BarChart3, MoreHorizontal, Check, 
  MessageSquare, RefreshCw, Clock, Shield, AlertTriangle, BookOpen, Target,
  Sparkles, Plus, Pencil, Trash2, Search, X, Save, ChevronUp, ChevronDown, Filter
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { API } from "../App";

const AdminDashboard = ({ auth }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [userProgress, setUserProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [activeTab, setActiveTab] = useState("subscribers");
  const [userLimit, setUserLimit] = useState(500);
  
  // Content Management State
  const [questions, setQuestions] = useState([]);
  const [flashcards, setFlashcards] = useState([]);
  const [questionSearch, setQuestionSearch] = useState("");
  const [flashcardSearch, setFlashcardSearch] = useState("");
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editingFlashcard, setEditingFlashcard] = useState(null);
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [showAddFlashcard, setShowAddFlashcard] = useState(false);
  const [newQuestion, setNewQuestion] = useState({
    question_text: "",
    options: { A: "", B: "", C: "", D: "" },
    correct_answer: "A",
    explanation: "",
    chapter: "",
    difficulty: "medium"
  });
  const [newFlashcard, setNewFlashcard] = useState({
    front: "",
    back: "",
    category: "",
    difficulty: "medium"
  });
  
  // Sorting and Filtering State
  const [questionSort, setQuestionSort] = useState({ column: null, direction: "asc" });
  const [questionFilter, setQuestionFilter] = useState({ chapter: "", difficulty: "" });
  const [flashcardSort, setFlashcardSort] = useState({ column: null, direction: "asc" });
  const [flashcardFilter, setFlashcardFilter] = useState({ category: "", difficulty: "" });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = auth.token ? { Authorization: `Bearer ${auth.token}` } : {};
      
      const [statsRes, usersRes, contactsRes, subscribersRes, progressRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/users?limit=${userLimit}`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/contacts`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/subscribers`, { headers, withCredentials: true }),
        axios.get(`${API}/progress/admin/all-users`, { headers, withCredentials: true })
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data.users || []);
      setContacts(contactsRes.data || []);
      setSubscribers(subscribersRes.data.subscribers || []);
      setUserProgress(progressRes.data.users || []);
    } catch (error) {
      console.error("Failed to fetch admin data:", error);
      if (error.response?.status === 403) {
        setAccessDenied(true);
        toast.error("Admin access required");
      } else {
        toast.error("Failed to load admin data");
      }
    } finally {
      setLoading(false);
    }
  };

  const updateContactStatus = async (contactId, status) => {
    try {
      await axios.put(
        `${API}/admin/contacts/${contactId}/status`,
        { status },
        {
          headers: auth.token ? { Authorization: `Bearer ${auth.token}` } : {},
          withCredentials: true
        }
      );
      
      // Update local state
      setContacts(contacts.map(c => 
        c.contact_id === contactId ? { ...c, status } : c
      ));
      toast.success("Status updated");
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleChangeSubscription = async (userId, email, newTier) => {
    try {
      const headers = auth.token ? { Authorization: `Bearer ${auth.token}` } : {};
      await axios.put(
        `${API}/admin/users/${userId}/subscription`,
        { subscription_tier: newTier },
        { headers, withCredentials: true }
      );
      
      // Update local state for users list
      setUsers(users.map(u => 
        u.user_id === userId ? { ...u, subscription_tier: newTier } : u
      ));
      
      // Update subscribers list
      if (newTier === "free") {
        setSubscribers(subscribers.filter(s => s.user_id !== userId));
      } else {
        const existingSubscriber = subscribers.find(s => s.user_id === userId);
        if (!existingSubscriber) {
          // Refresh subscribers to get the updated list
          fetchData();
        } else {
          setSubscribers(subscribers.map(s => 
            s.user_id === userId ? { ...s, subscription_tier: newTier } : s
          ));
        }
      }
      
      toast.success(`${email} updated to ${newTier.toUpperCase()}`);
    } catch (error) {
      console.error("Failed to update subscription:", error);
      toast.error(error.response?.data?.detail || "Failed to update subscription");
    }
  };

  // Content Management Functions
  const fetchQuestions = async (search = "") => {
    try {
      const headers = auth.token ? { Authorization: `Bearer ${auth.token}` } : {};
      const params = new URLSearchParams({ limit: "10000" });
      if (search) params.append("search", search);
      
      const response = await axios.get(`${API}/admin/content/questions?${params}`, { headers });
      setQuestions(response.data.questions || []);
    } catch (error) {
      console.error("Failed to fetch questions:", error);
    }
  };

  const fetchFlashcards = async (search = "") => {
    try {
      const headers = auth.token ? { Authorization: `Bearer ${auth.token}` } : {};
      const params = new URLSearchParams({ limit: "10000" });
      if (search) params.append("search", search);
      
      const response = await axios.get(`${API}/admin/content/flashcards?${params}`, { headers });
      setFlashcards(response.data.flashcards || []);
    } catch (error) {
      console.error("Failed to fetch flashcards:", error);
    }
  };

  const handleSaveQuestion = async () => {
    try {
      const headers = auth.token ? { Authorization: `Bearer ${auth.token}` } : {};
      
      if (editingQuestion) {
        await axios.put(
          `${API}/admin/content/questions/${editingQuestion.question_id}`,
          editingQuestion,
          { headers }
        );
        setQuestions(questions.map(q => 
          q.question_id === editingQuestion.question_id ? editingQuestion : q
        ));
        toast.success("Question updated");
      } else {
        const response = await axios.post(`${API}/admin/content/questions`, newQuestion, { headers });
        setQuestions([response.data.question, ...questions]);
        setNewQuestion({
          question_text: "",
          options: { A: "", B: "", C: "", D: "" },
          correct_answer: "A",
          explanation: "",
          chapter: "",
          difficulty: "medium"
        });
        toast.success("Question created");
      }
      setEditingQuestion(null);
      setShowAddQuestion(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save question");
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm("Are you sure you want to delete this question?")) return;
    
    try {
      const headers = auth.token ? { Authorization: `Bearer ${auth.token}` } : {};
      await axios.delete(`${API}/admin/content/questions/${questionId}`, { 
        headers,
        withCredentials: true 
      });
      setQuestions(questions.filter(q => q.question_id !== questionId));
      toast.success("Question deleted successfully");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error.response?.data?.detail || "Failed to delete question");
    }
  };

  const handleSaveFlashcard = async () => {
    try {
      const headers = auth.token ? { Authorization: `Bearer ${auth.token}` } : {};
      
      if (editingFlashcard) {
        await axios.put(
          `${API}/admin/content/flashcards/${editingFlashcard.flashcard_id}`,
          editingFlashcard,
          { headers }
        );
        setFlashcards(flashcards.map(f => 
          f.flashcard_id === editingFlashcard.flashcard_id ? editingFlashcard : f
        ));
        toast.success("Flashcard updated");
      } else {
        const response = await axios.post(`${API}/admin/content/flashcards`, newFlashcard, { headers });
        setFlashcards([response.data.flashcard, ...flashcards]);
        setNewFlashcard({ front: "", back: "", category: "", difficulty: "medium" });
        toast.success("Flashcard created");
      }
      setEditingFlashcard(null);
      setShowAddFlashcard(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save flashcard");
    }
  };

  const handleDeleteFlashcard = async (flashcardId) => {
    if (!window.confirm("Are you sure you want to delete this flashcard?")) return;
    
    try {
      const headers = auth.token ? { Authorization: `Bearer ${auth.token}` } : {};
      await axios.delete(`${API}/admin/content/flashcards/${flashcardId}`, { 
        headers,
        withCredentials: true 
      });
      setFlashcards(flashcards.filter(f => f.flashcard_id !== flashcardId));
      toast.success("Flashcard deleted successfully");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error.response?.data?.detail || "Failed to delete flashcard");
    }
  };

  // Fetch content when tab changes
  useEffect(() => {
    if (activeTab === "questions" && questions.length === 0) {
      fetchQuestions();
    } else if (activeTab === "flashcards" && flashcards.length === 0) {
      fetchFlashcards();
    }
  }, [activeTab]);

  // Sorting function
  const handleSort = (type, column) => {
    if (type === "question") {
      setQuestionSort(prev => ({
        column,
        direction: prev.column === column && prev.direction === "asc" ? "desc" : "asc"
      }));
    } else {
      setFlashcardSort(prev => ({
        column,
        direction: prev.column === column && prev.direction === "asc" ? "desc" : "asc"
      }));
    }
  };

  // Get sorted and filtered questions
  const getFilteredQuestions = () => {
    let filtered = [...questions];
    
    // Apply filters
    if (questionFilter.chapter) {
      filtered = filtered.filter(q => 
        (q.chapter || q.chapter_id || "").toLowerCase().includes(questionFilter.chapter.toLowerCase())
      );
    }
    if (questionFilter.difficulty) {
      filtered = filtered.filter(q => q.difficulty === questionFilter.difficulty);
    }
    
    // Apply sorting
    if (questionSort.column) {
      filtered.sort((a, b) => {
        let aVal = a[questionSort.column] || "";
        let bVal = b[questionSort.column] || "";
        if (typeof aVal === "string") aVal = aVal.toLowerCase();
        if (typeof bVal === "string") bVal = bVal.toLowerCase();
        
        if (aVal < bVal) return questionSort.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return questionSort.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    
    return filtered;
  };

  // Get sorted and filtered flashcards
  const getFilteredFlashcards = () => {
    let filtered = [...flashcards];
    
    // Apply filters
    if (flashcardFilter.category) {
      filtered = filtered.filter(fc => 
        (fc.category || "").toLowerCase().includes(flashcardFilter.category.toLowerCase())
      );
    }
    if (flashcardFilter.difficulty) {
      filtered = filtered.filter(fc => fc.difficulty === flashcardFilter.difficulty);
    }
    
    // Apply sorting
    if (flashcardSort.column) {
      filtered.sort((a, b) => {
        let aVal = a[flashcardSort.column] || "";
        let bVal = b[flashcardSort.column] || "";
        if (typeof aVal === "string") aVal = aVal.toLowerCase();
        if (typeof bVal === "string") bVal = bVal.toLowerCase();
        
        if (aVal < bVal) return flashcardSort.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return flashcardSort.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    
    return filtered;
  };

  // Get unique values for filters
  const getUniqueChapters = () => [...new Set(questions.map(q => q.chapter || q.chapter_id).filter(Boolean))];
  const getUniqueCategories = () => [...new Set(flashcards.map(fc => fc.category).filter(Boolean))];

  // Sort indicator component
  const SortIndicator = ({ column, currentSort }) => {
    if (currentSort.column !== column) return <ChevronDown className="w-4 h-4 opacity-30" />;
    return currentSort.direction === "asc" 
      ? <ChevronUp className="w-4 h-4" /> 
      : <ChevronDown className="w-4 h-4" />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-CA", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD"
    }).format(amount);
  };

  const statusColors = {
    new: "bg-blue-100 text-blue-700",
    read: "bg-yellow-100 text-yellow-700",
    replied: "bg-green-100 text-green-700",
    resolved: "bg-slate-100 text-slate-700"
  };

  const tierColors = {
    free: "bg-slate-100 text-slate-700",
    pro: "bg-blue-100 text-blue-700",
    premium: "bg-yellow-100 text-yellow-700"
  };

  if (loading) {
    return (
      <DashboardLayout auth={auth}>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-maple" />
        </div>
      </DashboardLayout>
    );
  }

  if (accessDenied) {
    return (
      <DashboardLayout auth={auth}>
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="font-heading font-bold text-2xl text-slate-900 mb-2">Access Denied</h2>
          <p className="text-slate-600 mb-4">You don't have permission to access the admin dashboard.</p>
          <Button onClick={() => navigate("/dashboard")} variant="outline">
            Return to Dashboard
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout auth={auth}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-slate-900 flex items-center gap-2">
              <Shield className="w-7 h-7 text-maple" />
              {t("admin.title") || "Admin Dashboard"}
            </h1>
            <p className="text-slate-600 mt-1">
              Monitor users, subscriptions, and contact inquiries
            </p>
          </div>
          <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Overview */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card data-testid="total-users-stat">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">{t("admin.totalUsers") || "Total Users"}</p>
                  <p className="text-3xl font-bold text-slate-900">{stats?.users?.total || 0}</p>
                  <p className="text-xs text-green-600 mt-1">
                    +{stats?.users?.new_this_week || 0} {t("admin.newThisWeek") || "this week"}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="revenue-stat">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">{t("admin.totalRevenue") || "Total Revenue"}</p>
                  <p className="text-3xl font-bold text-slate-900">{formatCurrency(stats?.revenue?.total || 0)}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Pro: {formatCurrency(stats?.revenue?.by_tier?.pro || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="contacts-stat">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">{t("admin.totalContacts") || "Contact Inquiries"}</p>
                  <p className="text-3xl font-bold text-slate-900">{stats?.contacts?.total || 0}</p>
                  <p className="text-xs text-orange-600 mt-1">
                    {stats?.contacts?.new || 0} new
                  </p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                  <Mail className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="tests-stat">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Total Tests Taken</p>
                  <p className="text-3xl font-bold text-slate-900">{stats?.tests?.total_taken || 0}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    All time
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Subscription Breakdown */}
        <div className="grid gap-4 md:grid-cols-2">
          <Card data-testid="subscription-breakdown">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Crown className="w-5 h-5 text-yellow-500" />
                {t("admin.subscriptions") || "Subscriptions"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge className={tierColors.free}>Free</Badge>
                    <span className="text-sm text-slate-600">{t("admin.freeUsers") || "Free Users"}</span>
                  </div>
                  <span className="font-semibold text-slate-900">{stats?.users?.by_subscription?.free || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge className={tierColors.pro}>Pro</Badge>
                    <span className="text-sm text-slate-600">{t("admin.proUsers") || "Pro Subscribers"}</span>
                  </div>
                  <span className="font-semibold text-slate-900">{stats?.users?.by_subscription?.pro || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge className={tierColors.premium}>Premium</Badge>
                    <span className="text-sm text-slate-600">{t("admin.premiumUsers") || "Premium Subscribers"}</span>
                  </div>
                  <span className="font-semibold text-slate-900">{stats?.users?.by_subscription?.premium || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="language-breakdown">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Globe className="w-5 h-5 text-blue-500" />
                {t("admin.languageStats") || "Language Preferences"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">🇬🇧</span>
                    <span className="text-sm text-slate-600">{t("admin.englishUsers") || "English Users"}</span>
                  </div>
                  <span className="font-semibold text-slate-900">{stats?.users?.by_language?.english || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">🇫🇷</span>
                    <span className="text-sm text-slate-600">{t("admin.frenchUsers") || "French Users"}</span>
                  </div>
                  <span className="font-semibold text-slate-900">{stats?.users?.by_language?.french || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for Users and Contacts */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="flex-wrap h-auto gap-1">
            <TabsTrigger value="subscribers" className="flex items-center gap-2" data-testid="subscribers-tab">
              <Crown className="w-4 h-4" />
              Paid Subscribers
              {subscribers.length > 0 && (
                <Badge className="bg-green-500 text-white ml-1 px-1.5 py-0.5 text-xs">
                  {subscribers.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="activity" className="flex items-center gap-2" data-testid="activity-tab">
              <Target className="w-4 h-4" />
              User Activity
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2" data-testid="users-tab">
              <Users className="w-4 h-4" />
              All Users
            </TabsTrigger>
            <TabsTrigger value="questions" className="flex items-center gap-2" data-testid="questions-tab">
              <BookOpen className="w-4 h-4" />
              Questions
            </TabsTrigger>
            <TabsTrigger value="flashcards" className="flex items-center gap-2" data-testid="flashcards-tab">
              <Sparkles className="w-4 h-4" />
              Flashcards
            </TabsTrigger>
            <TabsTrigger value="contacts" className="flex items-center gap-2" data-testid="contacts-tab">
              <MessageSquare className="w-4 h-4" />
              Contacts
              {stats?.contacts?.new > 0 && (
                <Badge className="bg-red-500 text-white ml-1 px-1.5 py-0.5 text-xs">
                  {stats.contacts.new}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Paid Subscribers Tab */}
          <TabsContent value="subscribers">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Crown className="w-5 h-5 text-yellow-500" />
                  Paid Subscribers
                </CardTitle>
                <CardDescription>
                  Users who have purchased Pro or Premium subscriptions
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Amount Paid</TableHead>
                      <TableHead>Start Date</TableHead>
                      <TableHead>Expiry</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subscribers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                          <div className="flex flex-col items-center gap-2">
                            <Crown className="w-8 h-8 text-slate-300" />
                            <p>No paid subscribers yet</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      subscribers.map((subscriber) => (
                        <TableRow key={subscriber.user_id} data-testid={`subscriber-row-${subscriber.user_id}`}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                                {subscriber.name?.charAt(0)?.toUpperCase() || "?"}
                              </div>
                              <span className="font-medium">{subscriber.name || "Unknown"}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-600">{subscriber.email}</TableCell>
                          <TableCell>
                            <Badge className={subscriber.subscription_tier === "premium" 
                              ? "bg-gradient-to-r from-yellow-400 to-orange-500 text-white" 
                              : "bg-blue-100 text-blue-700"
                            }>
                              {subscriber.subscription_tier === "premium" ? "⭐ Premium" : "Pro"}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-semibold text-green-600">
                            {subscriber.amount_paid ? formatCurrency(subscriber.amount_paid) : "—"}
                          </TableCell>
                          <TableCell className="text-slate-600 text-sm">
                            {formatDate(subscriber.subscription_start || subscriber.payment_date)}
                          </TableCell>
                          <TableCell>
                            {subscriber.subscription_expiry === "Lifetime" ? (
                              <Badge className="bg-purple-100 text-purple-700">Lifetime</Badge>
                            ) : subscriber.subscription_expiry ? (
                              <span className="text-slate-600 text-sm">
                                {formatDate(subscriber.subscription_expiry)}
                              </span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* User Activity Tab */}
          <TabsContent value="activity">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Target className="w-5 h-5 text-blue-500" />
                  User Activity & Progress
                </CardTitle>
                <CardDescription>
                  Track chapter practice, simulation tests, and mock test progress for all users
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>User</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Chapter Practice</TableHead>
                      <TableHead>Simulations</TableHead>
                      <TableHead>Mock Tests</TableHead>
                      <TableHead>Last Active</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {userProgress.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                          <div className="flex flex-col items-center gap-2">
                            <Target className="w-8 h-8 text-slate-300" />
                            <p>No user activity data yet</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      userProgress.map((user) => (
                        <TableRow key={user.user_id} data-testid={`progress-row-${user.user_id}`}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                                {user.name?.charAt(0)?.toUpperCase() || "?"}
                              </div>
                              <div>
                                <p className="font-medium text-sm">{user.name || "Unknown"}</p>
                                <p className="text-xs text-slate-500">{user.email}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              user.subscription_tier === "premium" 
                                ? "bg-gradient-to-r from-yellow-400 to-orange-500 text-white" 
                                : user.subscription_tier === "pro"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-slate-100 text-slate-600"
                            }>
                              {user.subscription_tier || "free"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <p className="font-medium text-slate-900">
                                {user.chapter_practice?.total_questions_attempted || 0} questions
                              </p>
                              <p className="text-xs text-green-600">
                                {user.chapter_practice?.accuracy || 0}% accuracy
                              </p>
                              <p className="text-xs text-slate-500">
                                {user.chapter_practice?.chapters_started || 0} chapters started
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <p className="font-medium text-slate-900">
                                {user.simulations?.total_completed || 0} completed
                              </p>
                              {user.simulations?.total_completed > 0 && (
                                <>
                                  <p className="text-xs text-blue-600">
                                    Best: {user.simulations?.best_score || 0}%
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    {user.simulations?.passed_count || 0} passed
                                  </p>
                                </>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <p className="font-medium text-slate-900">
                                {user.mock_tests?.total_completed || 0} completed
                              </p>
                              {user.mock_tests?.total_completed > 0 && (
                                <>
                                  <p className="text-xs text-blue-600">
                                    Best: {user.mock_tests?.best_score || 0}%
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    {user.mock_tests?.passed_count || 0} passed
                                  </p>
                                </>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">
                            {user.last_activity ? formatDate(user.last_activity) : "—"}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="users">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Users className="w-5 h-5 text-blue-500" />
                      All Users
                    </CardTitle>
                    <CardDescription>
                      Showing {users.length} users
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">Show:</span>
                    <Select 
                      value={userLimit.toString()} 
                      onValueChange={(value) => {
                        setUserLimit(parseInt(value));
                        // Refetch with new limit
                        setTimeout(() => fetchData(), 100);
                      }}
                    >
                      <SelectTrigger className="w-32" data-testid="user-limit-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="100">100 users</SelectItem>
                        <SelectItem value="500">500 users</SelectItem>
                        <SelectItem value="1000">1,000 users</SelectItem>
                        <SelectItem value="5000">5,000 users</SelectItem>
                        <SelectItem value="10000">10,000 users</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Language</TableHead>
                      <TableHead>Subscription</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                          No users found
                        </TableCell>
                      </TableRow>
                    ) : (
                      users.map((user) => (
                        <TableRow key={user.user_id} data-testid={`user-row-${user.user_id}`}>
                          <TableCell className="font-medium">{user.name}</TableCell>
                          <TableCell className="text-slate-600">{user.email}</TableCell>
                          <TableCell>
                            <span className="text-lg">{user.language === "fr" ? "🇫🇷" : "🇬🇧"}</span>
                          </TableCell>
                          <TableCell>
                            <Badge className={tierColors[user.subscription_tier || "free"]}>
                              {user.subscription_tier || "free"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-600 text-sm">
                            {formatDate(user.created_at)}
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" data-testid={`user-actions-${user.user_id}`}>
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem 
                                  onClick={() => handleChangeSubscription(user.user_id, user.email, "free")}
                                  disabled={user.subscription_tier === "free"}
                                >
                                  <Badge className={tierColors.free + " mr-2"}>Free</Badge>
                                  Set as Free
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleChangeSubscription(user.user_id, user.email, "pro")}
                                  disabled={user.subscription_tier === "pro"}
                                >
                                  <Badge className={tierColors.pro + " mr-2"}>Pro</Badge>
                                  Set as Pro
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleChangeSubscription(user.user_id, user.email, "premium")}
                                  disabled={user.subscription_tier === "premium"}
                                >
                                  <Badge className={tierColors.premium + " mr-2"}>Premium</Badge>
                                  Set as Premium
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Questions Management Tab */}
          <TabsContent value="questions">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <BookOpen className="w-5 h-5 text-blue-500" />
                      Questions Management
                    </CardTitle>
                    <CardDescription>
                      Showing {getFilteredQuestions().length} of {questions.length} questions
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search questions..."
                        className="pl-9 w-48"
                        value={questionSearch}
                        onChange={(e) => setQuestionSearch(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && fetchQuestions(questionSearch)}
                      />
                    </div>
                    <Select 
                      value={questionFilter.chapter} 
                      onValueChange={(v) => setQuestionFilter({...questionFilter, chapter: v === "all" ? "" : v})}
                    >
                      <SelectTrigger className="w-36">
                        <SelectValue placeholder="All Chapters" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Chapters</SelectItem>
                        {getUniqueChapters().map(ch => (
                          <SelectItem key={ch} value={ch}>{ch}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select 
                      value={questionFilter.difficulty} 
                      onValueChange={(v) => setQuestionFilter({...questionFilter, difficulty: v === "all" ? "" : v})}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="Difficulty" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="easy">Easy</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="hard">Hard</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button onClick={() => fetchQuestions(questionSearch)} variant="outline" size="sm">
                      <Search className="w-4 h-4" />
                    </Button>
                    <Button onClick={() => setShowAddQuestion(true)} className="bg-maple hover:bg-maple-600">
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0 max-h-[600px] overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-white z-10">
                    <TableRow>
                      <TableHead 
                        className="w-[400px] cursor-pointer hover:bg-slate-50"
                        onClick={() => handleSort("question", "question_text")}
                      >
                        <div className="flex items-center gap-1">
                          Question
                          <SortIndicator column="question_text" currentSort={questionSort} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => handleSort("question", "chapter")}
                      >
                        <div className="flex items-center gap-1">
                          Chapter
                          <SortIndicator column="chapter" currentSort={questionSort} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => handleSort("question", "correct_answer")}
                      >
                        <div className="flex items-center gap-1">
                          Answer
                          <SortIndicator column="correct_answer" currentSort={questionSort} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => handleSort("question", "difficulty")}
                      >
                        <div className="flex items-center gap-1">
                          Difficulty
                          <SortIndicator column="difficulty" currentSort={questionSort} />
                        </div>
                      </TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getFilteredQuestions().length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                          No questions found
                        </TableCell>
                      </TableRow>
                    ) : (
                      getFilteredQuestions().map((q) => (
                        <TableRow key={q.question_id}>
                          <TableCell className="font-medium text-sm">
                            {q.question_text?.substring(0, 100)}...
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{q.chapter || q.chapter_id || "N/A"}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className="bg-green-100 text-green-700">{q.correct_answer}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              q.difficulty === "hard" ? "bg-red-100 text-red-700" :
                              q.difficulty === "easy" ? "bg-blue-100 text-blue-700" :
                              "bg-yellow-100 text-yellow-700"
                            }>
                              {q.difficulty || "medium"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => setEditingQuestion(q)}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteQuestion(q.question_id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Flashcards Management Tab */}
          <TabsContent value="flashcards">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Sparkles className="w-5 h-5 text-purple-500" />
                      Flashcards Management
                    </CardTitle>
                    <CardDescription>
                      Showing {getFilteredFlashcards().length} of {flashcards.length} flashcards
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search flashcards..."
                        className="pl-9 w-48"
                        value={flashcardSearch}
                        onChange={(e) => setFlashcardSearch(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && fetchFlashcards(flashcardSearch)}
                      />
                    </div>
                    <Select 
                      value={flashcardFilter.category} 
                      onValueChange={(v) => setFlashcardFilter({...flashcardFilter, category: v === "all" ? "" : v})}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="All Categories" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {getUniqueCategories().map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select 
                      value={flashcardFilter.difficulty} 
                      onValueChange={(v) => setFlashcardFilter({...flashcardFilter, difficulty: v === "all" ? "" : v})}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="Difficulty" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="easy">Easy</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="hard">Hard</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button onClick={() => fetchFlashcards(flashcardSearch)} variant="outline" size="sm">
                      <Search className="w-4 h-4" />
                    </Button>
                    <Button onClick={() => setShowAddFlashcard(true)} className="bg-purple-600 hover:bg-purple-700">
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0 max-h-[600px] overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-white z-10">
                    <TableRow>
                      <TableHead 
                        className="w-[300px] cursor-pointer hover:bg-slate-50"
                        onClick={() => handleSort("flashcard", "front")}
                      >
                        <div className="flex items-center gap-1">
                          Front (Question)
                          <SortIndicator column="front" currentSort={flashcardSort} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="w-[300px] cursor-pointer hover:bg-slate-50"
                        onClick={() => handleSort("flashcard", "back")}
                      >
                        <div className="flex items-center gap-1">
                          Back (Answer)
                          <SortIndicator column="back" currentSort={flashcardSort} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => handleSort("flashcard", "category")}
                      >
                        <div className="flex items-center gap-1">
                          Category
                          <SortIndicator column="category" currentSort={flashcardSort} />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => handleSort("flashcard", "difficulty")}
                      >
                        <div className="flex items-center gap-1">
                          Difficulty
                          <SortIndicator column="difficulty" currentSort={flashcardSort} />
                        </div>
                      </TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getFilteredFlashcards().length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                          No flashcards found
                        </TableCell>
                      </TableRow>
                    ) : (
                      getFilteredFlashcards().map((fc) => (
                        <TableRow key={fc.flashcard_id}>
                          <TableCell className="font-medium text-sm">
                            {fc.front?.substring(0, 80)}...
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">
                            {fc.back?.substring(0, 80)}...
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{fc.category || "General"}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              fc.difficulty === "hard" ? "bg-red-100 text-red-700" :
                              fc.difficulty === "easy" ? "bg-blue-100 text-blue-700" :
                              "bg-yellow-100 text-yellow-700"
                            }>
                              {fc.difficulty || "medium"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => setEditingFlashcard(fc)}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteFlashcard(fc.flashcard_id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="contacts">
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Subject</TableHead>
                      <TableHead>Message</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="w-[70px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contacts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                          No contact submissions
                        </TableCell>
                      </TableRow>
                    ) : (
                      contacts.map((contact) => (
                        <TableRow key={contact.contact_id} data-testid={`contact-row-${contact.contact_id}`}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{contact.name}</p>
                              <p className="text-xs text-slate-500">{contact.email}</p>
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{contact.subject}</TableCell>
                          <TableCell className="max-w-xs truncate text-slate-600 text-sm">
                            {contact.message}
                          </TableCell>
                          <TableCell>
                            <Badge className={statusColors[contact.status || "new"]}>
                              {contact.status || "new"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-600 text-sm">
                            {formatDate(contact.created_at)}
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem 
                                  onClick={() => updateContactStatus(contact.contact_id, "read")}
                                >
                                  <Check className="w-4 h-4 mr-2" />
                                  {t("admin.markAsRead") || "Mark as Read"}
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => updateContactStatus(contact.contact_id, "replied")}
                                >
                                  <MessageSquare className="w-4 h-4 mr-2" />
                                  {t("admin.markAsReplied") || "Mark as Replied"}
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => updateContactStatus(contact.contact_id, "resolved")}
                                >
                                  <Check className="w-4 h-4 mr-2" />
                                  {t("admin.resolve") || "Resolve"}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Add/Edit Question Dialog */}
        <Dialog open={showAddQuestion || editingQuestion !== null} onOpenChange={(open) => {
          if (!open) {
            setShowAddQuestion(false);
            setEditingQuestion(null);
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingQuestion ? "Edit Question" : "Add New Question"}</DialogTitle>
              <DialogDescription>
                {editingQuestion ? "Update the question details below" : "Fill in the question details below"}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium">Question Text</label>
                <Textarea
                  placeholder="Enter the question..."
                  className="mt-1"
                  rows={3}
                  value={editingQuestion?.question_text || newQuestion.question_text}
                  onChange={(e) => editingQuestion 
                    ? setEditingQuestion({...editingQuestion, question_text: e.target.value})
                    : setNewQuestion({...newQuestion, question_text: e.target.value})
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                {["A", "B", "C", "D"].map((opt) => (
                  <div key={opt}>
                    <label className="text-sm font-medium">Option {opt}</label>
                    <Input
                      placeholder={`Option ${opt}...`}
                      className="mt-1"
                      value={editingQuestion?.options?.[opt] || newQuestion.options[opt]}
                      onChange={(e) => {
                        if (editingQuestion) {
                          setEditingQuestion({
                            ...editingQuestion,
                            options: {...editingQuestion.options, [opt]: e.target.value}
                          });
                        } else {
                          setNewQuestion({
                            ...newQuestion,
                            options: {...newQuestion.options, [opt]: e.target.value}
                          });
                        }
                      }}
                    />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium">Correct Answer</label>
                  <Select 
                    value={editingQuestion?.correct_answer || newQuestion.correct_answer}
                    onValueChange={(value) => editingQuestion
                      ? setEditingQuestion({...editingQuestion, correct_answer: value})
                      : setNewQuestion({...newQuestion, correct_answer: value})
                    }
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A">A</SelectItem>
                      <SelectItem value="B">B</SelectItem>
                      <SelectItem value="C">C</SelectItem>
                      <SelectItem value="D">D</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Chapter</label>
                  <Input
                    placeholder="e.g., ch1"
                    className="mt-1"
                    value={editingQuestion?.chapter || newQuestion.chapter}
                    onChange={(e) => editingQuestion
                      ? setEditingQuestion({...editingQuestion, chapter: e.target.value})
                      : setNewQuestion({...newQuestion, chapter: e.target.value})
                    }
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Difficulty</label>
                  <Select 
                    value={editingQuestion?.difficulty || newQuestion.difficulty}
                    onValueChange={(value) => editingQuestion
                      ? setEditingQuestion({...editingQuestion, difficulty: value})
                      : setNewQuestion({...newQuestion, difficulty: value})
                    }
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="easy">Easy</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="hard">Hard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Explanation (Optional)</label>
                <Textarea
                  placeholder="Explain why this is the correct answer..."
                  className="mt-1"
                  rows={2}
                  value={editingQuestion?.explanation || newQuestion.explanation}
                  onChange={(e) => editingQuestion
                    ? setEditingQuestion({...editingQuestion, explanation: e.target.value})
                    : setNewQuestion({...newQuestion, explanation: e.target.value})
                  }
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowAddQuestion(false);
                setEditingQuestion(null);
              }}>
                Cancel
              </Button>
              <Button onClick={handleSaveQuestion} className="bg-maple hover:bg-maple-600">
                <Save className="w-4 h-4 mr-1" />
                {editingQuestion ? "Update Question" : "Create Question"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add/Edit Flashcard Dialog */}
        <Dialog open={showAddFlashcard || editingFlashcard !== null} onOpenChange={(open) => {
          if (!open) {
            setShowAddFlashcard(false);
            setEditingFlashcard(null);
          }
        }}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>{editingFlashcard ? "Edit Flashcard" : "Add New Flashcard"}</DialogTitle>
              <DialogDescription>
                {editingFlashcard ? "Update the flashcard details below" : "Fill in the flashcard details below"}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium">Front (Question/Term)</label>
                <Textarea
                  placeholder="Enter the question or term..."
                  className="mt-1"
                  rows={3}
                  value={editingFlashcard?.front || newFlashcard.front}
                  onChange={(e) => editingFlashcard
                    ? setEditingFlashcard({...editingFlashcard, front: e.target.value})
                    : setNewFlashcard({...newFlashcard, front: e.target.value})
                  }
                />
              </div>
              <div>
                <label className="text-sm font-medium">Back (Answer/Definition)</label>
                <Textarea
                  placeholder="Enter the answer or definition..."
                  className="mt-1"
                  rows={3}
                  value={editingFlashcard?.back || newFlashcard.back}
                  onChange={(e) => editingFlashcard
                    ? setEditingFlashcard({...editingFlashcard, back: e.target.value})
                    : setNewFlashcard({...newFlashcard, back: e.target.value})
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Category</label>
                  <Input
                    placeholder="e.g., History, Geography"
                    className="mt-1"
                    value={editingFlashcard?.category || newFlashcard.category}
                    onChange={(e) => editingFlashcard
                      ? setEditingFlashcard({...editingFlashcard, category: e.target.value})
                      : setNewFlashcard({...newFlashcard, category: e.target.value})
                    }
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Difficulty</label>
                  <Select 
                    value={editingFlashcard?.difficulty || newFlashcard.difficulty}
                    onValueChange={(value) => editingFlashcard
                      ? setEditingFlashcard({...editingFlashcard, difficulty: value})
                      : setNewFlashcard({...newFlashcard, difficulty: value})
                    }
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="easy">Easy</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="hard">Hard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowAddFlashcard(false);
                setEditingFlashcard(null);
              }}>
                Cancel
              </Button>
              <Button onClick={handleSaveFlashcard} className="bg-purple-600 hover:bg-purple-700">
                <Save className="w-4 h-4 mr-1" />
                {editingFlashcard ? "Update Flashcard" : "Create Flashcard"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
